import { useEffect, useState } from 'react';
import { Container, Card, CardContent, Grid, Stack, TextField, MenuItem, Box, Dialog } from '@mui/material';
import { ArrowBackOutlined as ArrowBackOutlinedIcon } from '@mui/icons-material';
import { fetchMyPortfolio, uploadImage } from '../../lib/api';
import { track } from '../../lib/analytics';
import { navigate } from '../../lib/navigation';
import { useOwnedSection } from '../../lib/useOwnedSection';
import Button from '../../components/Button';
import ConfirmDialog from '../../components/ConfirmDialog';
import PageHeader from '../../components/PageHeader';
import SectionLoading from '../../components/SectionLoading';
import PortfolioThumbnail from '../../components/PortfolioThumbnail';

function EditPortfolioPage({ id, identityActions }) {
  const { items: portfolio, loading, setItems: setPortfolio } = useOwnedSection(fetchMyPortfolio, '/v1/profiles/me/portfolio', (next) => ({
    portfolio: next,
  }));
  const [form, setForm] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

  const existing = !loading && portfolio.find((item) => item.id === id);

  useEffect(() => {
    if (existing && !form) setForm(existing);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [existing]);

  if (loading || !form) return <SectionLoading />;

  if (!existing) {
    navigate('/dashboard/portfolio');
    return null;
  }

  async function handleThumbnailUpload(file) {
    if (!file) return;
    setUploading(true);
    try {
      const { url } = await uploadImage(file);
      setForm((current) => ({ ...current, thumbnailUrl: url }));
    } catch (error) {
      identityActions.showErrorToast(error.message || 'Image upload failed');
    } finally {
      setUploading(false);
    }
  }

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <PageHeader
        title="Edit content sample"
        description="Update the details for this content sample."
        action={
          <Stack direction="row" spacing={2}>
            <Button variant="secondary" onClick={() => navigate('/dashboard/portfolio')} startIcon={<ArrowBackOutlinedIcon />}>
              Back to samples
            </Button>
            <Button variant="danger" onClick={() => setDeleteConfirmOpen(true)}>
              Delete
            </Button>
          </Stack>
        }
      />
      <ConfirmDialog
        open={deleteConfirmOpen}
        title="Delete content sample?"
        description="This will permanently remove this content sample from your profile. This can't be undone."
        onClose={() => setDeleteConfirmOpen(false)}
        onConfirm={async () => {
          track('portfolio_item_deleted', { entityType: 'portfolio', entityId: id });
          await setPortfolio((current) => current.filter((item) => item.id !== id));
          identityActions.showToast('Content sample deleted');
          navigate('/dashboard/portfolio');
        }}
      />
      <Card variant="outlined">
        <CardContent>
          <Grid container spacing={2}>
            <Grid size={12}>
              <TextField
                fullWidth
                label="Title"
                placeholder="30-day skincare routine"
                helperText="Use a short title that explains what the content is about."
                value={form.title}
                onChange={(event) => setForm({ ...form, title: event.target.value })}
              />
            </Grid>
            <Grid size={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Description"
                placeholder="A short-form product tutorial created for a sensitive-skin audience."
                helperText="Mention the concept, your contribution, and any notable result."
                value={form.description}
                onChange={(event) => setForm({ ...form, description: event.target.value })}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                select
                fullWidth
                label="Content type"
                value={form.type}
                onChange={(event) => setForm({ ...form, type: event.target.value })}
              >
                {[
                  'Short-form video',
                  'Long-form video',
                  'Image',
                  'Carousel',
                  'Article',
                  'Livestream',
                  'UGC sample',
                  'Case study',
                  'Other',
                ].map((item) => (
                  <MenuItem key={item} value={item}>
                    {item}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                select
                fullWidth
                label="Platform"
                value={form.platform}
                onChange={(event) => setForm({ ...form, platform: event.target.value })}
              >
                {['TikTok', 'Instagram', 'Douyin', 'Xiaohongshu', 'Other'].map((item) => (
                  <MenuItem key={item} value={item}>
                    {item}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box
                  onClick={() => form.thumbnailUrl && setPreviewOpen(true)}
                  sx={{
                    width: 96,
                    borderRadius: 1,
                    overflow: 'hidden',
                    flexShrink: 0,
                    cursor: form.thumbnailUrl ? 'pointer' : 'default',
                  }}
                >
                  <PortfolioThumbnail item={form} height={64} showPlatformIcon={false} />
                </Box>
                <Button variant="secondary" component="label" disabled={uploading}>
                  {uploading ? 'Uploading…' : 'Upload image preview (optional)'}
                  <input hidden type="file" accept="image/*" onChange={(event) => handleThumbnailUpload(event.target.files?.[0])} />
                </Button>
              </Box>
            </Grid>
            <Dialog open={previewOpen} onClose={() => setPreviewOpen(false)} maxWidth="sm">
              <Box component="img" src={form.thumbnailUrl} alt="Content sample preview" sx={{ width: '100%', display: 'block' }} />
            </Dialog>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                type="date"
                label="Publication date"
                slotProps={{ inputLabel: { shrink: true } }}
                value={form.publishedAt}
                onChange={(event) => setForm({ ...form, publishedAt: event.target.value })}
              />
            </Grid>
            <Grid size={12}>
              <TextField
                fullWidth
                required
                label="Content URL"
                helperText="Link to the live post so brands can view the original content."
                value={form.contentUrl}
                onChange={(event) => setForm({ ...form, contentUrl: event.target.value })}
              />
            </Grid>
            <Grid size={12}>
              <Button
                onClick={async () => {
                  if (!form.title) return identityActions.showErrorToast('Content title is required');
                  if (!form.contentUrl) return identityActions.showErrorToast('Content URL is required');
                  track('portfolio_item_updated', { entityType: 'portfolio', entityId: id });
                  await setPortfolio((current) => current.map((item) => (item.id === id ? form : item)));
                  identityActions.showToast('Portfolio item updated');
                  navigate('/dashboard/portfolio');
                }}
              >
                Save changes
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Container>
  );
}

export default EditPortfolioPage;
